/**
 * Where a place is, by whatever means are available.
 *
 * A stored coordinate is always preferred: the organiser dropped that pin on
 * the exact side of the exact junction, and no search can reproduce that. It
 * also costs nothing to use.
 *
 * When the route carries no coordinate — which is the case today, because the
 * public data source exposes only the addresses — the address is looked up with
 * the same Nominatim client the organiser's location field already uses. No new
 * service, no key.
 *
 * Two things that matter about doing this on a PUBLIC page:
 *
 *  • **Results are cached per address for the life of the page**, so two
 *    activities starting at the same place cost one lookup, and a re-render
 *    costs none. Nominatim asks callers to stay near one request a second.
 *  • **A failure is silent and final.** No map is drawn and the address still
 *    reads underneath it, exactly as it did before any of this existed. A
 *    search that lands on the wrong "RV Road" would be a confident lie, so a
 *    low-confidence answer is treated the same as no answer.
 */

import { useEffect, useState } from 'react';
import { geocode } from '../geocode';
import parsePoint from './geoPoint';

// Shared across every map on the page: address → point, or null for "looked up
// and found nothing", which must not be retried on each render.
const cache = new Map();

export default function useResolvedPoint(point, address) {
  const stored = parsePoint(point);
  const key = typeof address === 'string' ? address.trim() : '';

  const [found, setFound] = useState(() => (stored ? null : cache.get(key) ?? null));

  useEffect(() => {
    // A stored coordinate wins and asks nothing of the network.
    if (stored || !key) return undefined;

    if (cache.has(key)) {
      setFound(cache.get(key));
      return undefined;
    }

    let alive = true;
    const controller = new AbortController();

    geocode(key, { signal: controller.signal }).then(results => {
      const first = Array.isArray(results) ? results[0] : null;
      const resolved = first ? parsePoint({ lat: first.lat, lng: first.lng ?? first.lon }) : null;
      cache.set(key, resolved);
      if (alive) setFound(resolved);
    });

    return () => { alive = false; controller.abort(); };
    // `stored` is an object rebuilt each render; its identity would restart the
    // effect forever, so the boolean is what the effect actually depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(stored), key]);

  return stored || found;
}

/** Test seam: forget what has been looked up. */
export function clearPointCache() {
  cache.clear();
}
